import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
	Button,
	Chip,
	IconButton,
	makeStyles,
	MenuItem,
	Paper,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { GetApp, OpenInNew } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	summary: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	hint: {
		margin: theme.spacing(0, 0, 1.5),
		color: theme.palette.text.secondary,
	},
	filters: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	field: {
		flex: "1 1 150px",
		minWidth: 130,
	},
	search: {
		flex: "2 1 220px",
	},
	muted: {
		display: "block",
		color: theme.palette.text.secondary,
	},
	queueDot: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		marginRight: theme.spacing(0.75),
	},
	empty: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
}));

const RATING_FILTERS = ["rated", "unrated", "low", "5", "4", "3", "2", "1"];

// "2026-10-18" -> start / end of that day in the browser's time zone
const dayStart = value => (value ? new Date(`${value}T00:00:00`).toISOString() : undefined);
const dayEnd = value => (value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined);

const LOW_STYLE = { backgroundColor: "#c62828", color: "#fff" };

const ratingColor = rating => (rating >= 4 ? "primary" : "default");

const Protocols = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const isAdmin = user.profile === "admin";

	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [queueId, setQueueId] = useState("");
	const [userId, setUserId] = useState("");
	const [whatsappId, setWhatsappId] = useState("");
	const [reasonId, setReasonId] = useState("");
	const [rating, setRating] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	const [data, setData] = useState({
		items: [],
		total: 0,
		summary: { total: 0, rated: 0, average: null, low: 0 },
	});
	const [loaded, setLoaded] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [queues, setQueues] = useState([]);
	const [users, setUsers] = useState([]);
	const [reasons, setReasons] = useState([]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 400);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		api
			.get("/queue")
			.then(({ data: result }) => setQueues(result || []))
			.catch(toastError);
		api
			.get("/close-reasons", { params: { all: "true" } })
			.then(({ data: result }) => setReasons(result.reasons || []))
			.catch(toastError);
		if (isAdmin) {
			api
				.get("/users/", { params: { pageNumber: 1 } })
				.then(({ data: result }) => setUsers(result.users || []))
				.catch(toastError);
		}
	}, [isAdmin]);

	const filterParams = useCallback(
		() => ({
			from: dayStart(from),
			to: dayEnd(to),
			queueId: queueId || undefined,
			userId: userId || undefined,
			whatsappId: whatsappId || undefined,
			closeReasonId: reasonId || undefined,
			rating: rating || undefined,
			search: search || undefined,
		}),
		[from, to, queueId, userId, whatsappId, reasonId, rating, search]
	);

	const load = useCallback(async () => {
		try {
			const { data: result } = await api.get("/protocols", {
				params: { ...filterParams(), page: page + 1, pageSize },
			});
			setData(result);
		} catch (err) {
			toastError(err);
		} finally {
			setLoaded(true);
		}
	}, [filterParams, page, pageSize]);

	useEffect(() => {
		load();
	}, [load]);

	const onFilter = setter => e => {
		setter(e.target.value);
		setPage(0);
	};

	const exportCsv = async () => {
		setExporting(true);
		try {
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const response = await api.get("/protocols/export", {
				params: { ...filterParams(), tz },
				responseType: "blob",
			});
			const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
			const link = document.createElement("a");
			link.href = url;
			link.download = "protocolos.csv";
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			if (response.headers["x-truncated"] === "true") {
				toast.warn(i18n.t("protocols.truncated", { rows: response.headers["x-rows"] }));
			}
		} catch (err) {
			toastError(err);
		}
		setExporting(false);
	};

	const { summary } = data;

	return (
		<MainContainer>
			<MainHeader>
				<Title>{i18n.t("protocols.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="contained"
						color="primary"
						startIcon={<GetApp />}
						disabled={exporting || data.total === 0}
						onClick={exportCsv}
					>
						{i18n.t("protocols.export")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<Paper className={classes.mainPaper} variant="outlined">
				<div className={classes.summary}>
					<Chip
						size="small"
						variant="outlined"
						label={`${i18n.t("protocols.summary.total")}: ${summary.total}`}
					/>
					<Chip
						size="small"
						variant="outlined"
						label={`${i18n.t("protocols.summary.rated")}: ${summary.rated}`}
					/>
					<Chip
						size="small"
						variant="outlined"
						label={`${i18n.t("protocols.summary.average")}: ${
							summary.average === null ? "—" : `${String(summary.average).replace(".", ",")}/5`
						}`}
					/>
					<Chip
						size="small"
						variant={summary.low > 0 ? "default" : "outlined"}
						style={summary.low > 0 ? LOW_STYLE : undefined}
						label={`${i18n.t("protocols.summary.low")}: ${summary.low}`}
					/>
				</div>
				<Typography variant="body2" className={classes.hint}>
					{isAdmin ? i18n.t("protocols.hintAdmin") : i18n.t("protocols.hintOwn")}
				</Typography>

				<div className={classes.filters}>
					<TextField
						className={classes.search}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.search")}
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
					/>
					<TextField
						type="date"
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.from")}
						value={from}
						onChange={onFilter(setFrom)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						type="date"
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.to")}
						value={to}
						onChange={onFilter(setTo)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.rating")}
						value={rating}
						onChange={onFilter(setRating)}
					>
						<MenuItem value="">{i18n.t("protocols.filters.anyRating")}</MenuItem>
						{RATING_FILTERS.map(r => (
							<MenuItem key={r} value={r}>
								{i18n.t(`protocols.ratingFilter.${r}`)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.reason")}
						value={reasonId}
						onChange={onFilter(setReasonId)}
					>
						<MenuItem value="">{i18n.t("protocols.filters.allReasons")}</MenuItem>
						<MenuItem value="none">{i18n.t("protocols.filters.noReason")}</MenuItem>
						{reasons.map(r => (
							<MenuItem key={r.id} value={r.id}>
								{r.name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.queue")}
						value={queueId}
						onChange={onFilter(setQueueId)}
					>
						<MenuItem value="">{i18n.t("protocols.filters.allQueues")}</MenuItem>
						<MenuItem value="none">{i18n.t("protocols.filters.noQueue")}</MenuItem>
						{queues.map(q => (
							<MenuItem key={q.id} value={q.id}>
								{q.name}
							</MenuItem>
						))}
					</TextField>
					{isAdmin && (
						<TextField
							select
							className={classes.field}
							size="small"
							variant="outlined"
							label={i18n.t("protocols.filters.attendant")}
							value={userId}
							onChange={onFilter(setUserId)}
						>
							<MenuItem value="">{i18n.t("protocols.filters.allAttendants")}</MenuItem>
							{users.map(u => (
								<MenuItem key={u.id} value={u.id}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
					)}
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("protocols.filters.connection")}
						value={whatsappId}
						onChange={onFilter(setWhatsappId)}
					>
						<MenuItem value="">{i18n.t("protocols.filters.allConnections")}</MenuItem>
						{whatsApps.map(w => (
							<MenuItem key={w.id} value={w.id}>
								{w.name}
							</MenuItem>
						))}
					</TextField>
				</div>

				{loaded && data.items.length === 0 ? (
					<div className={classes.empty}>
						<Typography variant="body1">{i18n.t("protocols.empty")}</Typography>
					</div>
				) : (
					<ResponsiveTable>
						<TableHead>
							<TableRow>
								<TableCell>{i18n.t("protocols.table.protocol")}</TableCell>
								<TableCell>{i18n.t("protocols.table.contact")}</TableCell>
								<TableCell>{i18n.t("protocols.table.attendant")}</TableCell>
								<TableCell>{i18n.t("protocols.table.queue")}</TableCell>
								<TableCell>{i18n.t("protocols.table.reason")}</TableCell>
								<TableCell align="center">{i18n.t("protocols.table.rating")}</TableCell>
								<TableCell>{i18n.t("protocols.table.closedAt")}</TableCell>
								<TableCell align="center">{i18n.t("protocols.table.actions")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map(item => (
								<TableRow key={item.id} hover>
									<TableCell data-label={i18n.t("protocols.table.protocol")}>
										{item.protocol || `#${item.id}`}
									</TableCell>
									<TableCell>
										{item.contactName || "—"}
										<span className={classes.muted}>{item.contactNumber}</span>
										<span className={classes.muted}>{item.whatsappName}</span>
									</TableCell>
									<TableCell data-label={i18n.t("protocols.table.attendant")}>
										{item.userName || "—"}
									</TableCell>
									<TableCell data-label={i18n.t("protocols.table.queue")}>
										{item.queueName ? (
											<span>
												<span
													className={classes.queueDot}
													style={{ backgroundColor: item.queueColor || "#999" }}
												/>
												{item.queueName}
											</span>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell data-label={i18n.t("protocols.table.reason")}>
										{item.closeReasonName || "—"}
									</TableCell>
									<TableCell align="center" data-label={i18n.t("protocols.table.rating")}>
										{item.rating ? (
											<Tooltip
												title={
													item.ratedAt ? new Date(item.ratedAt).toLocaleString() : ""
												}
											>
												<Chip
													size="small"
													color={ratingColor(item.rating)}
													style={item.rating <= 2 ? LOW_STYLE : undefined}
													variant={item.rating <= 2 || item.rating >= 4 ? "default" : "outlined"}
													label={`${item.rating}/5`}
												/>
											</Tooltip>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell data-label={i18n.t("protocols.table.closedAt")}>
										{new Date(item.closedAt).toLocaleString()}
									</TableCell>
									<TableCell align="center">
										<Tooltip title={i18n.t("protocols.openTicket")}>
											<IconButton
												size="small"
												onClick={() => history.push(`/tickets/${item.id}`)}
											>
												<OpenInNew fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</ResponsiveTable>
				)}

				{data.total > 0 && (
					<TablePagination
						component="div"
						count={data.total}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={[25, 50, 100]}
						labelRowsPerPage={i18n.t("protocols.perPage")}
						onChangePage={(_, next) => setPage(next)}
						onChangeRowsPerPage={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						labelDisplayedRows={({ from: a, to: b, count }) => `${a}–${b} / ${count}`}
					/>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Protocols;
